import type { RevertOutput, SceneView, ShotView } from "@dramapie/shared";
import { useState, type RefObject } from "react";

import { formatDuration, formatPoints } from "../../lib/format";
import type { StreamHub, StreamStatus } from "../../lib/stream";
import { DIALOGUE_TYPE_LABEL, SHOT_STATUS_META, shortId } from "../../lib/status";
import { AgentPanel } from "../AgentPanel";
import { HistoryPane } from "./HistoryPane";

export type InspectorTab = "agent" | "shot" | "history";

export interface InspectorProps {
  projectId: string;
  events?: StreamHub;
  streamStatus: StreamStatus;
  activeShot: ShotView | null;
  activeScene: SceneView | null;
  quickPrompts: string[];
  /** 右栏是否展开；收起时整块移出无障碍树（也避免窄屏抽屉占位）。 */
  open: boolean;
  /** 抽屉模式下的焦点落点。 */
  panelRef?: RefObject<HTMLElement>;
  /** Agent 执行中 → 顶栏入口亮圆点。 */
  onBusyChange?: (busy: boolean) => void;
  onClose: () => void;
  onConfirmShot: (shotId: string) => void;
  onRegenerateShot: (shotId: string) => void;
  /** 当前故事板 rev：历史面板据此标「当前」并作为回滚 `baseRev`。 */
  currentRev: number | null;
  /** 回滚成功：页面刷新 view 并 toast（历史面板只管请求）。 */
  onReverted: (output: RevertOutput, toRev: number) => void;
  /** 受控 tab：画布 / 时间线点镜头时页面把右栏切到「镜头」。 */
  activeTab?: InspectorTab;
  /** 切 tab：页面据此在打开「历史」时拉一次最新版本。 */
  onTabChange?: (tab: InspectorTab) => void;
  /** 镜头面板的「在画布看」：M6 后可用，切到画布并定位该镜。 */
  onOpenCanvas?: (shotId: string) => void;
}

const TABS: { id: InspectorTab; label: string }[] = [
  { id: "agent", label: "Agent" },
  { id: "shot", label: "镜头" },
  { id: "history", label: "历史" },
];

function ShotPane({
  shot,
  scene,
  onConfirm,
  onRegenerate,
  onOpenCanvas,
}: {
  shot: ShotView | null;
  scene: SceneView | null;
  onConfirm: (shotId: string) => void;
  onRegenerate: (shotId: string) => void;
  onOpenCanvas?: (shotId: string) => void;
}) {
  if (!shot) {
    return <p className="dp-pane-empty">在故事板上点一张卡，或点镜头带上的色块。</p>;
  }

  const meta = SHOT_STATUS_META[shot.status];
  const code = shortId(shot.id);

  return (
    <div className="dp-shot-pane dp-scroll">
      <div className="dp-sp-head">
        <div className="dp-sp-thumb">
          {shot.keyframe?.url ? (
            <img src={shot.keyframe.url} alt={`${code} 首帧缩略图`} loading="lazy" />
          ) : (
            <div
              role="img"
              aria-label={`${code} 首帧占位`}
              style={{
                aspectRatio: "9 / 16",
                display: "grid",
                placeItems: "center",
                color: "var(--ink-4)",
                fontFamily: "var(--mono)",
                fontSize: 12,
              }}
            >
              待生成
            </div>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="dp-sp-id">{code}</div>
          <div className={`dp-shot-status is-${meta.tone}`} style={{ marginTop: 4 }}>
            <i aria-hidden="true" />
            {meta.label}
          </div>
          <div className="dp-sp-meta">
            {shot.framing ? <span>{shot.framing}</span> : null}
            <span>{formatDuration(shot.duration)}</span>
            <span>v{shot.rev}</span>
            {scene ? <span>{scene.name}</span> : null}
          </div>
        </div>
      </div>

      {shot.action ? (
        <div className="dp-sp-section">
          <p className="dp-sp-label">动作</p>
          <p className="dp-sp-text">{shot.action}</p>
        </div>
      ) : null}

      {shot.dialogue.length > 0 ? (
        <div className="dp-sp-section">
          <p className="dp-sp-label">台词</p>
          <div className="dp-sp-dialogues">
            {shot.dialogue.map((line, index) => (
              <p key={`${shot.id}-sp-line-${index}`} className="dp-sp-dialogue">
                <b>{line.who}</b>
                <span aria-hidden="true">{DIALOGUE_TYPE_LABEL[line.type]}·</span>
                {line.text}
              </p>
            ))}
          </div>
        </div>
      ) : null}

      {shot.checks.length > 0 ? (
        <div className="dp-sp-section">
          <p className="dp-sp-label">
            Checks {shot.checks.filter((check) => check.pass).length}/{shot.checks.length}
          </p>
          {shot.checks.map((check, index) => (
            <p key={`${shot.id}-sp-check-${index}`} className="dp-check">
              <span className={`mark ${check.pass ? "ok" : "bad"}`} aria-hidden="true">
                {check.pass ? "✓" : "✗"}
              </span>
              <span className="dp-sr">{check.pass ? "通过" : "未过"}</span>
              <span>{check.q}</span>
            </p>
          ))}
        </div>
      ) : null}

      <div className="dp-sp-cost">
        <span>
          成本 <b>{shot.costPoints > 0 ? `${formatPoints(shot.costPoints)} 点` : "—"}</b>
        </span>
        <span>
          角色 <b>{shot.chars.length > 0 ? shot.chars.map(shortId).join(" / ") : "—"}</b>
        </span>
      </div>

      <div className="dp-sp-section" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button
          type="button"
          className="dp-btn sm"
          disabled={shot.status === "approved"}
          onClick={() => onConfirm(shot.id)}
        >
          确认
        </button>
        <button type="button" className="dp-btn sm" onClick={() => onRegenerate(shot.id)}>
          重抽
        </button>
        <button
          type="button"
          className="dp-btn sm"
          disabled={!onOpenCanvas}
          title={onOpenCanvas ? "切到画布并定位这一镜" : "画布还没准备好"}
          onClick={() => onOpenCanvas?.(shot.id)}
        >
          在画布看
        </button>
      </div>
    </div>
  );
}

/**
 * 右栏（366px）：`Agent | 镜头 | 历史`。
 * Agent 面板始终挂载（切 tab 用 CSS 隐藏），否则流式条目和草稿会丢。
 */
export function Inspector({
  projectId,
  events,
  streamStatus,
  activeShot,
  activeScene,
  quickPrompts,
  open,
  panelRef,
  onBusyChange,
  onClose,
  onConfirmShot,
  onRegenerateShot,
  currentRev,
  onReverted,
  activeTab,
  onTabChange,
  onOpenCanvas,
}: InspectorProps) {
  const [internalTab, setInternalTab] = useState<InspectorTab>("agent");
  // 受控 tab 由页面持有（画布 / 时间线点镜头时直接切到「镜头」）。
  const tab = activeTab ?? internalTab;

  function selectTab(next: InspectorTab) {
    if (activeTab === undefined) setInternalTab(next);
    onTabChange?.(next);
  }

  return (
    <aside
      className="dp-inspector"
      id="workspace-inspector"
      aria-label="Agent 与数据"
      hidden={!open}
      ref={panelRef}
    >
      <div className="dp-insp-tabs" role="tablist" aria-label="右栏视图">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`insp-tab-${item.id}`}
            aria-selected={tab === item.id}
            aria-controls={`insp-pane-${item.id}`}
            className="dp-insp-tab"
            onClick={() => {
              selectTab(item.id);
            }}
          >
            {item.label}
          </button>
        ))}
        <span className="grow" />
        <button type="button" className="dp-icon-btn" aria-label="关闭右栏" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="dp-insp-body">
        <div
          id="insp-pane-agent"
          role="tabpanel"
          aria-labelledby="insp-tab-agent"
          className={`dp-agent${tab === "agent" ? "" : " dp-pane-hidden"}`}
          hidden={tab !== "agent"}
        >
          <AgentPanel
            projectId={projectId}
            events={events}
            streamStatus={streamStatus}
            quickPrompts={quickPrompts}
            onBusyChange={onBusyChange}
          />
        </div>

        <div
          id="insp-pane-shot"
          role="tabpanel"
          aria-labelledby="insp-tab-shot"
          hidden={tab !== "shot"}
          style={{ display: tab === "shot" ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          <ShotPane
            shot={activeShot}
            scene={activeScene}
            onConfirm={onConfirmShot}
            onRegenerate={onRegenerateShot}
            onOpenCanvas={onOpenCanvas}
          />
        </div>

        <div
          id="insp-pane-history"
          role="tabpanel"
          aria-labelledby="insp-tab-history"
          hidden={tab !== "history"}
          style={{ display: tab === "history" ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          <HistoryPane projectId={projectId} currentRev={currentRev} onReverted={onReverted} />
        </div>
      </div>
    </aside>
  );
}
